// src/components/website/EventPublicCard.js
// Public-facing event card for the published website card list view.

import React from 'react';
import { Monitor, MapPin } from 'lucide-react';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatEventDatePublic(event) {
  const { eventType, schedule } = event;
  const dateOpts = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };

  if (eventType === 'single' && schedule?.date) {
    const d = new Date(schedule.date + 'T12:00:00');
    return d.toLocaleDateString('en-US', dateOpts);
  }
  if (eventType === 'series' && schedule?.sessionDates?.length > 0) {
    const first = new Date(schedule.sessionDates[0] + 'T12:00:00');
    const startStr = first.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `Starting ${startStr} · ${schedule.sessionDates.length} sessions`;
  }
  if (eventType === 'multi_day' && schedule?.date && schedule?.endDate) {
    const start = new Date(schedule.date + 'T12:00:00');
    const end = new Date(schedule.endDate + 'T12:00:00');
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} — ${endStr}`;
  }
  return '';
}

function formatTimeRange(event) {
  const { eventType, schedule, timezone } = event;
  if (eventType === 'single' && schedule?.startTime && schedule?.endTime) {
    return `${formatTime12(schedule.startTime)} — ${formatTime12(schedule.endTime)} ${getTimezoneAbbr(timezone)}`;
  }
  if (eventType === 'series' && schedule?.recurringTime && schedule?.sessionDuration) {
    const endMinutes = parseTimeToMinutes(schedule.recurringTime) + (schedule.sessionDuration || 0);
    const endTime = minutesToTime(endMinutes);
    return `${formatTime12(schedule.recurringTime)} — ${formatTime12(endTime)} ${getTimezoneAbbr(timezone)}`;
  }
  return '';
}

function formatTime12(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function parseTimeToMinutes(time24) {
  if (!time24) return 0;
  const [h, m] = time24.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(mins) {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

function getTimezoneAbbr(tz) {
  if (!tz) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart?.value || '';
  } catch { return ''; }
}

function formatPrice(event) {
  const { pricingModel, pricing } = event;
  if (pricingModel === 'free') return 'Free';
  if (pricingModel === 'early_bird' && pricing?.earlyBirdPrice) {
    const now = new Date();
    const deadline = pricing.earlyBirdDeadline ? new Date(pricing.earlyBirdDeadline + 'T23:59:59') : null;
    if (deadline && now <= deadline) {
      return `From $${(pricing.earlyBirdPrice / 100).toFixed(2)}`;
    }
    return `$${((pricing.price || 0) / 100).toFixed(2)}`;
  }
  if (pricingModel === 'deposit' && pricing?.depositAmount) {
    return `$${(pricing.depositAmount / 100).toFixed(2)} deposit`;
  }
  if (pricing?.price) return `$${(pricing.price / 100).toFixed(2)}`;
  return '';
}

function getSpotsText(event) {
  const spots = (event.maxAttendees || 0) - (event.currentAttendees || 0);
  if (spots <= 0) return { text: 'Sold Out', color: '#D97706' };
  if (spots <= 5) return { text: `${spots} spot${spots !== 1 ? 's' : ''} left`, color: '#D97706' };
  return { text: `${spots} spots left`, color: '#6B7280' };
}

function isRegistrationClosed(event) {
  if (event.currentAttendees >= event.maxAttendees) return true;
  if (event.registrationDeadline) {
    return new Date() > new Date(event.registrationDeadline);
  }
  return false;
}

const GRADIENTS = [
  'linear-gradient(135deg, #7c3aed22, #a78bfa33)',
  'linear-gradient(135deg, #6366f122, #818cf833)',
  'linear-gradient(135deg, #8b5cf622, #c4b5fd33)',
  'linear-gradient(135deg, #bb974922, #d4a85533)',
];

function getGradient(id) {
  let hash = 0;
  for (let i = 0; i < (id || '').length; i++) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
    hash |= 0;
  }
  return GRADIENTS[Math.abs(hash) % GRADIENTS.length];
}

// ─── Component ──────────────────────────────────────────────────────────────

const EventPublicCard = ({ event, practitionerSlug }) => {
  const spotsInfo = getSpotsText(event);
  const soldOut = event.currentAttendees >= event.maxAttendees;
  const closed = isRegistrationClosed(event);
  const detailUrl = `/site/${practitionerSlug}/events/${event.slug}`;

  return (
    <div style={{
      background: '#FFFFFF', borderRadius: 12, overflow: 'hidden',
      border: '1px solid #E5E7EB',
      display: 'flex', flexDirection: 'column',
      transition: 'box-shadow 0.2s',
    }}
      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'}
      onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
    >
      {/* Cover image */}
      <div style={{
        height: 160,
        background: event.coverImage ? `url(${event.coverImage}) center/cover no-repeat` : getGradient(event.id),
      }} />

      {/* Body */}
      <div style={{ padding: '20px 20px 16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Title */}
        <h3 style={{
          fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 12px',
          fontFamily: 'Montserrat, sans-serif', lineHeight: 1.3,
        }}>
          {event.title}
        </h3>

        {/* Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          {/* Date */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ fontSize: 14, marginTop: 1 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
            </span>
            <span style={{ fontSize: 14, color: '#374151', fontFamily: 'Montserrat, sans-serif' }}>
              {formatEventDatePublic(event)}
            </span>
          </div>

          {/* Time */}
          {formatTimeRange(event) && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ fontSize: 14, marginTop: 1 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
              </span>
              <span style={{ fontSize: 14, color: '#374151', fontFamily: 'Montserrat, sans-serif' }}>
                {formatTimeRange(event)}
              </span>
            </div>
          )}

          {/* Format */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ marginTop: 1 }}>
              {(event.format === 'virtual' || event.format === 'hybrid')
                ? <Monitor size={14} color="#6B7280" />
                : <MapPin size={14} color="#6B7280" />
              }
            </span>
            <span style={{ fontSize: 14, color: '#374151', fontFamily: 'Montserrat, sans-serif' }}>
              {event.format === 'virtual' ? 'Virtual' : event.format === 'in_person' ? 'In-Person' : 'Hybrid'}
            </span>
          </div>
        </div>

        {/* Price + spots */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, marginTop: 'auto' }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#111827', fontFamily: 'Montserrat, sans-serif' }}>
            {formatPrice(event)}
          </span>
          <span style={{ fontSize: 13, fontWeight: 500, color: spotsInfo.color, fontFamily: 'Montserrat, sans-serif' }}>
            {spotsInfo.text}
          </span>
        </div>

        {/* Register button */}
        <a
          href={detailUrl}
          style={{
            display: 'block', textAlign: 'center', padding: '12px 20px',
            borderRadius: 8, fontWeight: 600, fontSize: 14,
            fontFamily: 'Montserrat, sans-serif', textDecoration: 'none',
            transition: 'opacity 0.15s',
            ...(soldOut || closed
              ? { background: '#E5E7EB', color: '#9CA3AF', cursor: 'default', pointerEvents: 'none' }
              : { background: '#bb9749', color: '#FFFFFF', cursor: 'pointer' }
            ),
          }}
        >
          {soldOut ? 'Sold Out' : closed ? 'Registration Closed' : 'Register →'}
        </a>
      </div>
    </div>
  );
};

export default EventPublicCard;
